import { EEventFormFieldType } from '__generated__/globalTypes';
import { EventFormFieldImage } from 'components/eventView/eventForm/EventFormFieldImage';
import { validateFormFieldAndReturn } from 'components/eventView/eventForm/formValidation';
import { ActionButton } from 'elements/eventView/atomic/ActionButton';
import { LargeTextValue } from 'elements/minute/sidebar/sidebarComponents/atomic/LargeTextValue';
import { S_TextNumberValue } from 'elements/minute/sidebar/sidebarComponents/atomic/TextNumberValue/TextNumberValue';
import { MarkdownContent } from 'elements/visual/MarkdownContent';
import { EEventForm_fields } from 'graphql/fragment/__generated__/EEventForm';
import { EFonts } from 'libs/styles/styling';
import { uniq } from 'lodash';
import React, { useEffect, useState } from 'react';
import { EWorldColor } from 'static/config/worldVariables';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

export type EventFormField = EEventForm_fields & {
    title: string | React.ReactNode;
};

interface Props {
    submitText?: string;
    guideId: string;
    className?: string;
    isLoading: boolean;
    formFields: EventFormField[];
    prefilledValues?: Record<string, unknown>;
    onSubmit?: (data: Record<string, unknown>) => void;
    onSubmitValidationError?: () => void;
}

export const EventFormFields = (props: Props): JSX.Element => {
    const [values, setValues] = useState(
        Object.fromEntries(
            props.formFields
                .map<[string, unknown]>((field) => {
                    if (
                        field.type === EEventFormFieldType.STRING ||
                        field.type === EEventFormFieldType.EMAIL ||
                        field.type === EEventFormFieldType.URL ||
                        field.type === EEventFormFieldType.IMAGE ||
                        field.type === EEventFormFieldType.STRING_MULTILINE
                    ) {
                        return [field.fieldId, props.prefilledValues?.[field.fieldId] ?? ''];
                    }
                    if (field.type === EEventFormFieldType.MULTI_OPTIONS) {
                        return [field.fieldId, props.prefilledValues?.[field.fieldId] ?? []];
                    }
                    if (field.type === EEventFormFieldType.BOOLEAN) {
                        return [field.fieldId, props.prefilledValues?.[field.fieldId] ?? false];
                    }
                    if (field.type === EEventFormFieldType.RADIO) {
                        return [field.fieldId, props.prefilledValues?.[field.fieldId] ?? null];
                    }
                    return null;
                })
                .filter((e) => e !== null)
        )
    );

    const [blurredFieldIds, setBlurredFieldIds] = useState<string[]>([]);
    const [visibleErrors, setVisibleErrors] = useState<[string, string][]>([]);
    const [canSubmit, setCanSubmit] = useState(false);

    function getAllErrors() {
        const allErrors = [];

        props.formFields.forEach((field) => {
            const value = values[field.fieldId];
            const valueOrError = validateFormFieldAndReturn(field, value);

            if (valueOrError.type === 'error') {
                allErrors.push([field.fieldId, valueOrError.message]);
            }
        });
        return allErrors;
    }

    useEffect(() => {
        const allErrors = getAllErrors();

        setVisibleErrors(allErrors.filter((e) => blurredFieldIds.includes(e[0])));
        setCanSubmit(allErrors.length === 0);
    }, [props.formFields, values, blurredFieldIds]);

    function submit() {
        if (!canSubmit) {
            const allFieldIds = props.formFields.map((f) => f.fieldId);
            setBlurredFieldIds(allFieldIds);
            props.onSubmitValidationError?.();
            return;
        }

        props.onSubmit(values);
    }

    const submitEnabled = canSubmit && !props.isLoading;

    return (
        <S_EventFormFields className={props.className}>
            {props.formFields.map((field) => {
                const value = values[field.fieldId];
                const error = visibleErrors.find((e) => e[0] === field.fieldId)?.[1] ?? null;

                if (field.type === EEventFormFieldType.HEADLINE) {
                    return <S_Headline key={field.fieldId}>{field.title}</S_Headline>;
                }
                if (field.type === EEventFormFieldType.INFO_TEXT) {
                    return (
                        <S_Section key={field.fieldId}>
                            {field.title && <S_Headline>{field.title}</S_Headline>}
                            <S_InfoText text={field.text} />
                        </S_Section>
                    );
                }
                if (
                    field.type === EEventFormFieldType.STRING ||
                    field.type === EEventFormFieldType.EMAIL ||
                    field.type === EEventFormFieldType.URL ||
                    field.type === EEventFormFieldType.STRING_MULTILINE
                ) {
                    const inputId = `input-${field.fieldId}`;
                    return (
                        <S_Section key={field.fieldId}>
                            <S_Headline>
                                {field.title}
                                {field.validation?.required && <S_StarRequired>*</S_StarRequired>}
                            </S_Headline>
                            <S_InputFieldContainer>
                                {field.type === EEventFormFieldType.STRING_MULTILINE ? (
                                    <S_TextMultiline
                                        id={inputId}
                                        value={value as string}
                                        placeholder={field.placeholder}
                                        minRows={3}
                                        maxRows={3}
                                        onChange={(newVal) => {
                                            setValues((curr) => ({
                                                ...curr,
                                                [field.fieldId]: newVal
                                            }));
                                        }}
                                        onBlur={() => {
                                            setBlurredFieldIds(
                                                uniq([...blurredFieldIds, field.fieldId])
                                            );
                                        }}
                                    />
                                ) : (
                                    <S_InputField
                                        id={inputId}
                                        value={value as string}
                                        placeholder={field.placeholder}
                                        type={
                                            field.type === EEventFormFieldType.EMAIL
                                                ? 'email'
                                                : 'text'
                                        }
                                        onChange={(e) => {
                                            setValues((curr) => ({
                                                ...curr,
                                                [field.fieldId]: e.target.value
                                            }));
                                        }}
                                        onBlur={() => {
                                            setBlurredFieldIds(
                                                uniq([...blurredFieldIds, field.fieldId])
                                            );
                                        }}
                                    />
                                )}
                                {error && <S_SectionError>{error}</S_SectionError>}
                            </S_InputFieldContainer>
                        </S_Section>
                    );
                }

                if (field.type === EEventFormFieldType.BOOLEAN) {
                    const inputId = `input-${field.fieldId}`;
                    return (
                        <S_Section key={field.fieldId}>
                            <S_OptionRow htmlFor={inputId}>
                                <S_OptionField
                                    id={inputId}
                                    type="checkbox"
                                    checked={value as boolean}
                                    onChange={(e) => {
                                        setValues((curr) => ({
                                            ...curr,
                                            [field.fieldId]: e.target.checked
                                        }));
                                    }}
                                />
                                {field.title}
                            </S_OptionRow>
                            {error && <S_SectionError>{error}</S_SectionError>}
                        </S_Section>
                    );
                }

                if (field.type === EEventFormFieldType.IMAGE) {
                    return (
                        <S_Section key={field.fieldId}>
                            <S_Headline>
                                {field.title}
                                {field.validation?.required && <S_StarRequired>*</S_StarRequired>}
                            </S_Headline>
                            <EventFormFieldImage
                                guideId={props.guideId}
                                value={values[field.fieldId] as string}
                                onChange={(imageUrl) => {
                                    setValues((curr) => ({
                                        ...curr,
                                        [field.fieldId]: imageUrl
                                    }));
                                }}
                                onReset={() => {
                                    setValues((curr) => ({
                                        ...curr,
                                        [field.fieldId]: ''
                                    }));
                                }}
                            />
                            {error && <S_SectionError>{error}</S_SectionError>}
                        </S_Section>
                    );
                }

                if (field.type === EEventFormFieldType.RADIO) {
                    const fieldsComponents = field.options.map((option) => {
                        const inputId = `input-${field.fieldId}-${option.optionId}`;
                        return (
                            <S_OptionRow key={option.optionId} htmlFor={inputId}>
                                <S_OptionField
                                    id={inputId}
                                    type="radio"
                                    checked={values[field.fieldId] === option.optionId}
                                    onChange={() => {
                                        setValues((curr) => ({
                                            ...curr,
                                            [field.fieldId]: option.optionId
                                        }));
                                    }}
                                />
                                {option.title}
                            </S_OptionRow>
                        );
                    });

                    return (
                        <S_Section>
                            <S_Headline>
                                {field.title}
                                {field.validation?.required && <S_StarRequired>*</S_StarRequired>}
                            </S_Headline>
                            {fieldsComponents}
                            {error && <S_SectionError>{error}</S_SectionError>}
                        </S_Section>
                    );
                }

                if (field.type === EEventFormFieldType.MULTI_OPTIONS) {
                    const fieldsComponents = field.options.map((option) => {
                        const inputId = `input-${field.fieldId}-${option.optionId}`;
                        return (
                            <S_OptionRow key={option.optionId} htmlFor={inputId}>
                                <S_OptionField
                                    id={inputId}
                                    type="checkbox"
                                    checked={(values[field.fieldId] as string[])?.includes(
                                        option.optionId
                                    )}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        const currentValues = values[field.fieldId] as string[];
                                        const newValues = newValue
                                            ? uniq([...currentValues, option.optionId])
                                            : currentValues.filter((e) => e !== option.optionId);

                                        setValues((curr) => ({
                                            ...curr,
                                            [field.fieldId]: newValues
                                        }));
                                    }}
                                />
                                {option.title}
                            </S_OptionRow>
                        );
                    });

                    return (
                        <S_Section key={field.fieldId}>
                            <S_Headline>
                                {field.title}
                                {field.validation?.required && <S_StarRequired>*</S_StarRequired>}
                            </S_Headline>
                            {fieldsComponents}
                            {error && <S_SectionError>{error}</S_SectionError>}
                        </S_Section>
                    );
                }
            })}

            <S_SubmitButton
                text={props.isLoading ? '' : props.submitText ?? Localization.FORM_SUBMIT}
                icon={props.isLoading ? { iconName: 'spinner' } : null}
                highlighted={submitEnabled}
                onClick={submit}
                $highlighted={submitEnabled}
                iconColor={submitEnabled ? '#041321' : null}
            />
        </S_EventFormFields>
    );
};

const S_EventFormFields = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Headline = styled.div`
    font-family: 'InterMedium';
    font-size: 15px;
    line-height: 24px;
    margin: 0 0 10px 0;
    &:first-child {
        margin-top: 0;
    }
`;

const S_InfoText = styled(MarkdownContent)``;

export const S_Section = styled.div`
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;
    position: relative;
    border-radius: 15px;
    background: #ffffff;
    padding: 15px;
    box-shadow: 5px 5px 15px 0px rgba(0, 0, 0, 0.01);
`;

const S_InputFieldContainer = styled.div`
    position: relative;
`;

export const S_InputField = styled(S_TextNumberValue)`
    outline: none;
    border-top: none;
    border-left: none;
    border-right: none;
    box-sizing: border-box;
    padding: 0 10px;
    border-radius: 7px;
    font-size: 12px;
    color: #141c54;
    font-family: ${EFonts.Regular};
    width: ${(props) => (props.width ? props.width : '100%')};
    font-size: 12px;
    border-bottom: 1px solid #dadada;
    background: #f5f5f5;
    box-shadow: 0px 0px 20px 0px #dedede inset;
    height: 40px;
    text-align: left;
    &:focus {
        background: #f5f5f5;
    }
    &::placeholder {
        color: #909090;
    }
`;

export const S_TextMultiline = styled(LargeTextValue)`
    outline: none;
    border-top: none;
    border-left: none;
    border-right: none;
    box-sizing: border-box;
    padding: 10px;
    border-radius: 7px;
    font-size: 12px;
    color: #141c54;
    font-family: ${EFonts.Regular};
    width: ${(props) => (props.width ? props.width : '100%')};
    font-size: 12px;
    border-bottom: 1px solid #dadada;
    background: #f5f5f5;
    box-shadow: 0px 0px 20px 0px #dedede inset;
    text-align: left;
    &:focus {
        background: #f5f5f5;
    }
    &::placeholder {
        color: #909090;
    }
    &::-webkit-scrollbar-track {
        background-color: #ccc;
    }
    &::-webkit-scrollbar-thumb {
        background-color: #909090;
        border-radius: 0px;
    }
`;

const S_SectionError = styled.div`
    background: #${EWorldColor.RED};
    color: #fff;
    font-size: 11px;
    font-family: ${EFonts.Medium};
    position: absolute;
    bottom: -11px;
    left: 0;
    right: 0;
    padding: 3px 12px;
    border-radius: 0 0 7px 7px;
`;

const S_OptionField = styled.input`
    border-radius: 7px;
    background: #f5f5f5;
    box-shadow: 0px 0px 20px 0px #dedede inset;
    margin: 0 6px 0 0;
    cursor: pointer;
    &:checked {
        box-shadow: none;
    }
`;

const S_OptionRow = styled.label`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 7px;
    margin-top: 7px;
    font-family: 'InterMedium';
    font-size: 15px;
    cursor: pointer;
`;

export const S_SubmitButton = styled(ActionButton)<{ $highlighted: boolean }>`
    align-self: center;
    width: 250px;
    margin-top: 20px;
    ${(props) => {
        if (props.$highlighted) {
            return css`
                background-color: #fcfc72;
            `;
        }
    }}
`;

const S_StarRequired = styled.span`
    color: #${EWorldColor.RED};
`;
